"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

interface ContattiHeroGraphicProps {
  isItalian: boolean;
}

const S = "hsl(var(--primary))";
const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * Grafica dell'hero Contatti: visualizza il titolo "dalla call a un piano chiaro".
 * Scena line-art (stile del sito): una bolla-conversazione ("La call", con
 * badge 30 min e puntini che scrivono) collegata da un impulso a un documento
 * "Il tuo piano" con un check che si disegna. Solo transform/opacity, gated
 * in-view, ferma con reduced-motion. Decorativa.
 */
export function ContattiHeroGraphic({ isItalian }: ContattiHeroGraphicProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const loop = inView && !reduce;

  return (
    <div ref={ref} className="relative w-full max-w-xl">
      <div className="pointer-events-none absolute inset-0 rounded-[2rem] bg-primary/5 blur-3xl" />
      <div className="relative overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-surface/40 px-6 py-8">
        <div className="pointer-events-none absolute inset-0 pattern-dots opacity-40" aria-hidden="true" />

        <svg
          viewBox="0 0 400 200"
          className="relative h-auto w-full"
          aria-hidden="true"
        >
          {/* ---- La call: bolla conversazione ---- */}
          <motion.g
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            <path
              d="M28 34 H150 A12 12 0 0 1 162 46 V96 A12 12 0 0 1 150 108 H70 L52 126 L56 108 H28 A12 12 0 0 1 16 96 V46 A12 12 0 0 1 28 34 Z"
              fill={S}
              fillOpacity={0.06}
              stroke={S}
              strokeWidth={1.6}
              strokeOpacity={0.7}
              strokeLinejoin="round"
            />
            {/* puntini "sta scrivendo" */}
            {[52, 74, 96].map((cx, i) => (
              <motion.circle
                key={cx}
                cx={cx}
                cy={72}
                r={5}
                fill={S}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
                initial={false}
                animate={loop ? { opacity: [0.3, 1, 0.3] } : { opacity: 0.7 }}
                transition={
                  loop
                    ? { duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }
                    : { duration: 0.3 }
                }
              />
            ))}
            {/* badge 30 min */}
            <g>
              <rect x={104} y={44} width={52} height={22} rx={11} fill={S} fillOpacity={0.14} stroke={S} strokeOpacity={0.5} strokeWidth={1} />
              <text x={130} y={59} textAnchor="middle" fontSize={12} fontWeight={700} fill={S}>30 min</text>
            </g>
          </motion.g>
          <text x={40} y={150} fontSize={12} letterSpacing={1} fill="hsl(var(--muted-foreground))">
            {isItalian ? "LA CALL" : "THE CALL"}
          </text>

          {/* ---- Connettore con impulso ---- */}
          <line x1={172} y1={78} x2={230} y2={78} stroke={S} strokeWidth={1.4} strokeDasharray="3 4" strokeOpacity={0.5} />
          <motion.circle
            cx={172}
            cy={78}
            r={3.4}
            fill={S}
            initial={false}
            animate={loop ? { cx: [172, 230], opacity: [0, 1, 0] } : { cx: 201, opacity: 0.8 }}
            transition={loop ? { duration: 1.6, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.4 } : { duration: 0.3 }}
          />

          {/* ---- Il tuo piano: documento con check ---- */}
          <motion.g
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
          >
            <rect x={242} y={34} width={132} height={92} rx={12} fill={S} fillOpacity={0.06} stroke={S} strokeWidth={1.6} strokeOpacity={0.7} />
            <rect x={258} y={52} width={72} height={6} rx={3} fill={S} fillOpacity={0.5} />
            <rect x={258} y={68} width={100} height={5} rx={2.5} fill={S} fillOpacity={0.3} />
            <rect x={258} y={82} width={84} height={5} rx={2.5} fill={S} fillOpacity={0.3} />
            {/* check che si disegna */}
            <motion.path
              d="M268 106 L277 114 L293 98"
              fill="none"
              stroke={S}
              strokeWidth={2.2}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={reduce ? false : { pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.7, delay: reduce ? 0 : 0.9, ease: "easeInOut" }}
            />
          </motion.g>
          <text x={264} y={150} fontSize={12} letterSpacing={1} fill="hsl(var(--muted-foreground))">
            {isItalian ? "IL TUO PIANO" : "YOUR PLAN"}
          </text>
        </svg>
      </div>
    </div>
  );
}
